/**
 * Overrides de tipo_evento por boleta (Consideraciones Previas).
 * El tareo en BD no cambia; solo el conteo/cálculo por destino.
 */

export const TIPOS_EVENTO_TAREO = [
  { value: 'asistio', label: 'Asistió' },
  { value: 'falta', label: 'Falta' },
  { value: 'feriado', label: 'Feriado' },
  { value: 'feriado_laborado', label: 'Feriado Laborado' },
  { value: 'descanso', label: 'Descanso' },
  { value: 'descanso_laborado', label: 'Descanso Laborado' },
  { value: 'descanso_medico', label: 'Descanso Médico' },
  { value: 'reposo_medico', label: 'Reposo Médico' },
  { value: 'subsidio_por_enfermedad', label: 'Subsidio por Enfermedad' },
  { value: 'permiso', label: 'Permiso' },
  { value: 'suspension', label: 'Suspensión' },
  { value: 'vacaciones', label: 'Vacaciones' },
  { value: 'tardanza', label: 'Tardanza' },
  { value: 'licencia_con_goce_de_haber', label: 'Lic. Goce de Haber' },
];

const TIPOS_SIN_HORAS = new Set([
  'vacaciones',
  'permiso',
  'suspension',
  'falta',
  'feriado',
  'descanso',
  'reposo_medico',
  'tardanza',
]);

const TIPOS_LABORADOS = new Set([
  'asistio',
  'feriado_laborado',
  'descanso_laborado',
]);

const TIPOS_OCHO_HORAS = new Set([
  'descanso_medico',
  'subsidio_por_enfermedad',
  'licencia_con_goce_de_haber',
]);

export function normalizarFechaTareo(fecha) {
  if (!fecha) return null;
  if (typeof fecha === 'string') return fecha.split('T')[0];
  try {
    return new Date(fecha).toISOString().split('T')[0];
  } catch {
    return null;
  }
}

export function claveOverrideTareo(empleadoId, fecha) {
  const f = normalizarFechaTareo(fecha);
  if (empleadoId == null || !f) return null;
  return `${Number(empleadoId)}|${f}`;
}

/**
 * Normaliza respuesta API a mapa empleadoId|fecha → { plame, interna, ... }
 */
export function mapaOverridesDesdeApi(data) {
  if (data?.mapa && typeof data.mapa === 'object') {
    const out = {};
    Object.entries(data.mapa).forEach(([key, row]) => {
      out[key] = {
        empleado_id: Number(row.empleado_id),
        fecha: normalizarFechaTareo(row.fecha),
        tipo_evento_plame: row.tipo_evento_plame || null,
        tipo_evento_interna: row.tipo_evento_interna || null,
      };
    });
    return out;
  }

  const out = {};
  (data?.overrides || []).forEach((row) => {
    const key = claveOverrideTareo(row.empleado_id, row.fecha);
    if (!key) return;
    out[key] = {
      empleado_id: Number(row.empleado_id),
      fecha: normalizarFechaTareo(row.fecha),
      tipo_evento_plame: row.tipo_evento_plame || null,
      tipo_evento_interna: row.tipo_evento_interna || null,
    };
  });
  return out;
}

/**
 * @param {object} tareo
 * @param {object|null} override fila { tipo_evento_plame, tipo_evento_interna }
 * @param {'plame'|'interna'} destino
 */
export function tipoEfectivo(tareo, override, destino = 'plame') {
  const original = String(tareo?.tipo_evento || tareo?.tipoEvento || 'asistio').toLowerCase().trim();
  if (!override) return original;

  const campo = destino === 'interna' ? 'tipo_evento_interna' : 'tipo_evento_plame';
  const ov = override[campo];
  if (ov == null || ov === '' || ov === 'usar_tareo') return original;
  return String(ov).toLowerCase().trim();
}

export function horasEfectivasParaTipo(tareo, tipoEfectivoValor, { modoFijas8 = false } = {}) {
  const tipo = String(tipoEfectivoValor || '').toLowerCase().trim();
  if (TIPOS_SIN_HORAS.has(tipo)) return 0;
  if (TIPOS_OCHO_HORAS.has(tipo)) return 8;
  if (TIPOS_LABORADOS.has(tipo)) {
    const h = parseFloat(tareo?.horas_trabajadas) || 0;
    if (modoFijas8) return 8;
    if (h > 0) return h;
    return 8;
  }
  return parseFloat(tareo?.horas_trabajadas) || 0;
}

/**
 * Clona tareos con tipo_evento (y horas) efectivos para un destino.
 * @param {Array} tareos
 * @param {object} mapaOverrides mapa de claveOverrideTareo
 * @param {'plame'|'interna'} destino
 * @param {{ modoFijas8?: boolean, empleadoId?: number }} opts
 */
export function aplicarOverridesTareos(tareos, mapaOverrides, destino = 'plame', opts = {}) {
  const mapa = mapaOverrides || {};
  return (tareos || []).map((tareo) => {
    const empId = opts.empleadoId != null
      ? opts.empleadoId
      : (tareo.empleado_id ?? tareo.empleadoId);
    const fecha = normalizarFechaTareo(tareo.fecha);
    const key = claveOverrideTareo(empId, fecha);
    const override = key ? mapa[key] : null;
    const tipo = tipoEfectivo(tareo, override, destino);
    const original = String(tareo.tipo_evento || tareo.tipoEvento || 'asistio').toLowerCase().trim();

    if (tipo === original && !override) {
      return tareo;
    }

    const horas = horasEfectivasParaTipo(tareo, tipo, { modoFijas8: Boolean(opts.modoFijas8) });

    return {
      ...tareo,
      tipo_evento: tipo,
      tipoEvento: tipo,
      tipo_evento_original: original,
      horas_trabajadas: horas,
      _override_destino: destino,
      _tiene_override_evento: Boolean(
        override
        && (
          (destino === 'interna' && override.tipo_evento_interna)
          || (destino === 'plame' && override.tipo_evento_plame)
        )
      ),
    };
  });
}

export default {
  TIPOS_EVENTO_TAREO,
  normalizarFechaTareo,
  claveOverrideTareo,
  mapaOverridesDesdeApi,
  tipoEfectivo,
  horasEfectivasParaTipo,
  aplicarOverridesTareos,
};

